/**
 * Toolbox Talks Module Routes
 *
 * All routes are nested under ToolboxTalksLayout.vue to provide a persistent
 * in-module horizontal sub-navigation bar across all Toolbox Talks views.
 */

import type { RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import type { AccessLevel } from '@/types/architecture'
import { usePermissionStore } from '@/stores/permissions'

async function checkAccessLevel(
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
) {
    const requiredLevel = (to.meta.minAccessLevel as AccessLevel | undefined) ?? 'view_only'
    const permissionStore = usePermissionStore()
    if (!permissionStore.isLoaded) {
        await permissionStore.load()
    }
    if (!permissionStore.can('toolbox_talks', requiredLevel)) {
        return next({ path: '/toolbox-talks' })
    }
    return next()
}

const routes: RouteRecordRaw[] = [
    {
        path: '/toolbox-talks',
        component: () => import('./components/ToolboxTalksLayout.vue'),
        meta: {
            moduleKey: 'toolbox_talks',
        },
        children: [
            {
                path: '',
                name: 'ToolboxTalkList',
                component: () => import('./views/TalkList.vue'),
                meta: {
                    title: 'Toolbox Talks',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: 'generate',
                name: 'ToolboxTalkGenerate',
                component: () => import('./views/AiGenerate.vue'),
                beforeEnter: checkAccessLevel,
                meta: {
                    title: 'Generate Toolbox Talk with AI',
                    moduleKey: 'toolbox_talks',
                    minAccessLevel: 'standard',
                },
            },
            {
                path: 'create',
                name: 'ToolboxTalkCreate',
                component: () => import('./views/TalkCreate.vue'),
                beforeEnter: checkAccessLevel,
                meta: {
                    title: 'Create Toolbox Talk',
                    moduleKey: 'toolbox_talks',
                    minAccessLevel: 'standard',
                },
            },
            {
                path: 'templates',
                name: 'ToolboxTalkTemplates',
                component: () => import('./views/TemplateLibrary.vue'),
                meta: {
                    title: 'Toolbox Talk Templates',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: 'templates/:id',
                name: 'ToolboxTalkTemplateDetail',
                component: () => import('./views/TemplateDetail.vue'),
                meta: {
                    title: 'Template Details',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: 'analytics',
                name: 'ToolboxTalkAnalytics',
                component: () => import('./views/AnalyticsDashboard.vue'),
                meta: {
                    title: 'Toolbox Talks Analytics',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: 'compliance',
                name: 'ToolboxTalkCompliance',
                component: () => import('./views/ComplianceReport.vue'),
                meta: {
                    title: 'OSHA Safety Compliance Report',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: 'badges',
                name: 'ToolboxTalkBadges',
                component: () => import('./views/WorkerBadges.vue'),
                meta: {
                    title: 'Worker QR Helmet Badges',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: 'categories',
                name: 'ToolboxTalkCategories',
                component: () => import('./views/CategoryManagement.vue'),
                beforeEnter: checkAccessLevel,
                meta: {
                    title: 'Safety Categories',
                    moduleKey: 'toolbox_talks',
                    minAccessLevel: 'full',
                },
            },
            {
                path: 'settings',
                name: 'ToolboxTalkSettings',
                component: () => import('./views/ToolboxTalkSettings.vue'),
                beforeEnter: checkAccessLevel,
                meta: {
                    title: 'Toolbox Talks Settings',
                    moduleKey: 'toolbox_talks',
                    minAccessLevel: 'full',
                },
            },
            {
                path: 'sessions/:sessionId/report',
                name: 'ToolboxTalkSessionReport',
                component: () => import('./views/SessionReport.vue'),
                meta: {
                    title: 'Session Compliance Report',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: ':id',
                name: 'ToolboxTalkDetail',
                component: () => import('./views/TalkDetail.vue'),
                meta: {
                    title: 'Toolbox Talk Details',
                    moduleKey: 'toolbox_talks',
                },
            },
            {
                path: ':id/deliver',
                name: 'ToolboxTalkDeliver',
                component: () => import('./views/SessionDelivery.vue'),
                beforeEnter: checkAccessLevel,
                meta: {
                    title: 'Deliver Toolbox Talk',
                    moduleKey: 'toolbox_talks',
                    minAccessLevel: 'standard',
                },
            },
        ],
    },
]

export default routes
